import { useCallback } from "react";
import { toNumber } from "../utils/numberUtils";
import { getOrCreatePeriod } from "../api/periods";

export default function useResourceWorkflow({
  companyApi,
  classificationTable,
  resourceReportForm,
  setResourceReportSaveState,
  setAddResourceReportOpen,
  setResourceReportDialogMode,
  economicScenarios,
  onResourceReportSaved,
}) {
  const prepareResourceReportSections = useCallback(() => {
    if (!resourceReportForm.resource_reserve_id) {
      return { error: "Can't save estimate: select a resource context first." };
    }
    if (!resourceReportForm.as_of_date && !resourceReportForm.estimate_period_unknown) {
      return { error: "Can't save estimate: add an as-of date." };
    }
    const commodityIds = classificationTable.commodity_ids || [];
    if (!commodityIds.length) {
      return { error: "Can't save estimate: select at least one commodity." };
    }
    const rowsWithValues = (classificationTable.rows || [])
      .map((row) => {
        const tonnage = `${row.tonnage_mt}`.trim();
        const perCommodityValues = commodityIds.map((commodityId) => ({
          commodity_id: commodityId,
          grade_value: row.grade_values?.[`${commodityId}`] ?? "",
          contained_value: row.contained_values?.[`${commodityId}`] ?? "",
        }));
        const hasAnyValues =
          tonnage ||
          perCommodityValues.some(
            (val) => `${val.grade_value}`.trim() || `${val.contained_value}`.trim()
          );
        if (!hasAnyValues) return null;
        return {
          classification_level: row.classification_level,
          tonnage_mt: row.tonnage_mt,
          values: perCommodityValues,
        };
      })
      .filter(Boolean);
    const hasAnyValues = rowsWithValues.length > 0;
    if (!hasAnyValues) {
      return { error: "Can't save estimate: enter values for at least one classification." };
    }
    const hasPartialRow = rowsWithValues.some((row) => {
      const tonnage = `${row.tonnage_mt}`.trim();
      if (!tonnage) return true;
      return row.values.some((value) => {
        const grade = `${value.grade_value}`.trim();
        const contained = `${value.contained_value}`.trim();
        return !(grade && contained);
      });
    });
    if (hasPartialRow) {
      return {
        error:
          "Can't save estimate: fill tonnage, grade, and contained for each classification you enter.",
      };
    }
    return { rowsWithValues };
  }, [classificationTable, resourceReportForm]);

  const submitResourceReport = useCallback(async () => {
    const { rowsWithValues, error } = prepareResourceReportSections();
    if (error) {
      setResourceReportSaveState({ loading: false, error, success: null });
      return;
    }
    setResourceReportSaveState({ loading: true, error: null, success: null });
    try {
      const period = resourceReportForm.as_of_date
        ? await getOrCreatePeriod({
            period_type: "as_of",
            as_of_date: resourceReportForm.as_of_date,
            label: resourceReportForm.as_of_date,
          })
        : null;
    const reportPayload = {
      resource_reserve: resourceReportForm.resource_reserve_id,
      period: period?.id || null,
      estimate_period_unknown: Boolean(resourceReportForm.estimate_period_unknown),
      source_title: resourceReportForm.source_title || "",
      source_url: resourceReportForm.source_url || "",
      source_notes: resourceReportForm.source_notes || "",
      economic_scenarios: economicScenarios,
    };
    const valueRows = rowsWithValues.flatMap((row) =>
      row.values.map((value) => ({
        commodity: value.commodity_id,
        classification_level: row.classification_level,
        tonnage_mt: toNumber(row.tonnage_mt),
        grade_value: toNumber(value.grade_value),
        contained_value: toNumber(value.contained_value),
      }))
    );
      const report = await companyApi.saveResourceReserveReportWithValues(
        reportPayload,
        valueRows
      );
      const values = report?.values || valueRows;
      setResourceReportSaveState({
        loading: false,
        error: null,
        success: "Resource report saved as draft.",
      });
      setAddResourceReportOpen(false);
      if (onResourceReportSaved) {
        onResourceReportSaved(resourceReportForm.resource_reserve_id, report, values);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.period ||
        "Can't save estimate. If you don't see a network call, check required fields.";
      setResourceReportSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    companyApi,
    economicScenarios,
    onResourceReportSaved,
    prepareResourceReportSections,
    resourceReportForm,
    setAddResourceReportOpen,
    setResourceReportSaveState,
  ]);

  const updateResourceReport = useCallback(async () => {
    if (!resourceReportForm.report_id) return;
    const { rowsWithValues, error } = prepareResourceReportSections();
    if (error) {
      setResourceReportSaveState({ loading: false, error, success: null });
      return;
    }
    setResourceReportSaveState({ loading: true, error: null, success: null });
    try {
      const period = resourceReportForm.as_of_date
        ? await getOrCreatePeriod({
            period_type: "as_of",
            as_of_date: resourceReportForm.as_of_date,
            label: resourceReportForm.as_of_date,
          })
        : null;
      const reportPayload = {
        period: period?.id || null,
        estimate_period_unknown: Boolean(resourceReportForm.estimate_period_unknown),
        source_title: resourceReportForm.source_title || "",
        source_url: resourceReportForm.source_url || "",
        source_notes: resourceReportForm.source_notes || "",
        economic_scenarios: economicScenarios,
      };
      const valueRows = rowsWithValues.flatMap((row) =>
        row.values.map((value) => ({
          commodity: value.commodity_id,
          classification_level: row.classification_level,
          tonnage_mt: toNumber(row.tonnage_mt),
          grade_value: toNumber(value.grade_value),
          contained_value: toNumber(value.contained_value),
        }))
      );
      const updatedReport = await companyApi.saveResourceReserveReportWithValues(
        reportPayload,
        valueRows,
        resourceReportForm.report_id
      );
      const values = updatedReport?.values || valueRows;
      setResourceReportSaveState({
        loading: false,
        error: null,
        success: "Resource report updated.",
      });
      setAddResourceReportOpen(false);
      setResourceReportDialogMode("create");
      if (onResourceReportSaved) {
        onResourceReportSaved(resourceReportForm.resource_reserve_id, updatedReport, values);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.period ||
        "Can't save estimate. If you don't see a network call, check required fields.";
      setResourceReportSaveState({ loading: false, error: detail, success: null });
    }
  }, [
    companyApi,
    economicScenarios,
    onResourceReportSaved,
    prepareResourceReportSections,
    resourceReportForm,
    setAddResourceReportOpen,
    setResourceReportDialogMode,
    setResourceReportSaveState,
  ]);

  return {
    submitResourceReport,
    updateResourceReport,
  };
}
